/**
 * Capture a still frame from a live <video> element produced by html5-qrcode
 * (or any other MediaStream-backed video). Used by G2.13 image-based product
 * lookup — the cashier taps "Match by image" inside the scanner overlay and
 * we send the resulting JPEG to /products/lookup/by-image.
 *
 * Implementation notes
 *  - We do NOT touch the scanner's internal canvas. We grab the video element
 *    by selector and draw it to a fresh offscreen canvas. This keeps the
 *    capture independent of html5-qrcode's internals.
 *  - We resize the longest edge down to `maxDim` to keep the upload small —
 *    perceptual hash is rotation/scale tolerant, so 720px is plenty.
 *  - On mobile Safari and Chrome, the video element occasionally has
 *    videoWidth/Height = 0 in the brief moment after the camera starts.
 *    We reject with a clear error so the caller can show "Camera still
 *    starting up — try again in a moment".
 */

const DEFAULT_MAX_DIM = 720;
const JPEG_QUALITY = 0.85;

export class CameraCaptureError extends Error {
    constructor(message: string) {
        super(message);
        this.name = 'CameraCaptureError';
    }
}

export async function captureFrameFromVideo(
    videoEl: HTMLVideoElement | null,
    maxDim: number = DEFAULT_MAX_DIM,
): Promise<Blob> {
    if (!videoEl) {
        throw new CameraCaptureError('No video element to capture from.');
    }
    const sourceWidth = videoEl.videoWidth;
    const sourceHeight = videoEl.videoHeight;
    if (!sourceWidth || !sourceHeight) {
        throw new CameraCaptureError(
            'Camera is still starting up — wait a moment and try again.',
        );
    }

    // Scale the longest edge to maxDim, preserving aspect ratio.
    const scale = Math.min(1, maxDim / Math.max(sourceWidth, sourceHeight));
    const targetWidth = Math.max(1, Math.round(sourceWidth * scale));
    const targetHeight = Math.max(1, Math.round(sourceHeight * scale));

    const canvas = document.createElement('canvas');
    canvas.width = targetWidth;
    canvas.height = targetHeight;

    const ctx = canvas.getContext('2d');
    if (!ctx) {
        throw new CameraCaptureError('Canvas 2D context not available in this browser.');
    }
    ctx.drawImage(videoEl, 0, 0, targetWidth, targetHeight);

    return new Promise<Blob>((resolve, reject) => {
        canvas.toBlob(
            (blob) => {
                if (!blob) {
                    reject(new CameraCaptureError('Failed to encode captured frame as JPEG.'));
                    return;
                }
                resolve(blob);
            },
            'image/jpeg',
            JPEG_QUALITY,
        );
    });
}
